<template>
  <Progressbar v-if="loading && !worktable.length" />
  <NetworkError v-else-if="errors && !worktable.length" @tryAgain="getData" />
  <div class="w-full h-full bg-gray-200 dark:bg-base-100 rounded-tr-3xl xl:px-9 pt-6 md:pt-10 pb-14" v-else>
    <div class="container h-full">
      <div class="flex justify-between items-center mb-6">
        <h3 class="flex items-center md:text-26 text-xl dark:text-white text-biscay-700 font-semibold">
          <i class="md:flex hidden w-2 h-2 dark:bg-white rounded-full bg-blue-700 ml-2"></i>
          {{ __("Worktable") }}
        </h3>
        <Dropdown labelClass="btn-outline" :label="selectedRoute?.title">
          <ul class="menu dropdown-content bg-base-300 rounded-box z-[2] w-36 p-2 shadow lg:!end-auto">
            <li v-for="route in worktable">
              <button @click="setRoute(route)">{{ route.title }}</button>
            </li>
          </ul>
        </Dropdown>
      </div>

      <div class="flex mt-4 lg:mt-8 overflow-x-auto h-[calc(100%-1rem)]">
        <div class="flex flex-col w-max mx-auto gap-3">
          <ul class="timeline w-full gap-3">
            <li v-for="route in selectedRoute.items" class="w-56">
              <hr class="bg-neutral" />
              <div class="timeline-start text-sm text-center">{{ route.title }}</div>
              <div class="timeline-middle">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor"
                  class="h-8 w-8 fill-success">
                  <path fill-rule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z"
                    clip-rule="evenodd" />
                </svg>
              </div>
              <div class="timeline-end text-sm text-center">
                {{ route.opportunities_count + " " + __("Number") }} -
                {{
                  route.opportunities_sum_amount?.currency() || 0
                }}
              </div>
              <hr class="bg-neutral" />
            </li>
          </ul>
          <span class="loading loading-spinner text-error loading-lg mt-32 mx-auto"
            v-if="directLoading && !opportunities.length"></span>
          <NetworkError v-else-if="errors" @tryAgain="getOpportunities(true)" />
          <ul class="timeline w-full gap-3 mt-4 h-full overflow-y-auto" v-else>
            <li v-for="route in selectedRoute.items" :key="route.id" class="w-56 relative">
              <div class="flex flex-col gap-3 h-full absolute left-0 right-0">
                <draggable :model-value="itemsByRoute(route)" group="tasks" item-key="id"
                  class="flex flex-col gap-3 min-h-[50px] h-full" :sort="false" @end="onDragEnd" @start="onDragStart"
                  @change="(evt) => onDragChange(evt, route)" :draggable="'.draggable-item'">
                  <template #item="{ element }">
                    <div class="card rounded-md bg-base-300 h-36 draggable-item cursor-move">
                      <div class="card-body p-3 gap-2 relative">
                        <div class="w-2 h-2 absolute rounded-full left-3 top-3 animate-pulse bg-yellow-300"
                          :title="__('You did not specify any activities.')" v-if="!element.is_appointment"></div>
                        <h4 class="font-bold">{{ element.name }}</h4>
                        <p class="text-sm">
                          {{ __("Person") }}: {{ element.person_name }}
                        </p>
                        <p class="text-xs text-error">
                          {{ __("Amount") }}:
                          {{
                            element.amount.currency
                              ? element.amount.currency(element.currency)
                              : element.amount + " " + element.currency
                          }}
                        </p>
                        <div class="card-actions justify-between items-center">
                          <small>{{ element.appointment_date }}</small>
                          <label for="menu-modal" @click="setItem(element)" class="btn btn-ghost btn-circle btn-sm">
                            <EllipsisVerticalIcon class="w-5 h-5" />
                          </label>
                        </div>
                      </div>
                    </div>
                  </template>

                  <template #header>
                    <div @click="setOpportunity('create', { route: route })"
                      class="card btn-ghost rounded-md bg-base-300 h-36 cursor-pointer flex items-center justify-center flex-shrink-0">
                      <div class="border border-dashed w-14 h-14 rounded-full flex justify-center items-center">
                        <PlusIcon class="w-8 h-8" />
                      </div>
                    </div>
                  </template>
                </draggable>
                <transition name="fade">
                  <div v-if="dragging" class="fixed bottom-6 left-1/2 transform -translate-x-1/2 flex gap-16 z-50">
                    <div
                      class="p-4 rounded-lg bg-green-500 bg-opacity-20 backdrop-blur flex items-center justify-center shadow cursor-pointer transition-all duration-200"
                      :class="{
                        'opacity-100 scale-110 bg-opacity-70':
                          hoverTarget === 'completed',
                      }" @dragover.prevent="hoverTarget = 'completed'" @dragleave.prevent="hoverTarget = null"
                      @drop.prevent="markItem(currentDraggedItem, 'completed')">
                      <CheckCircleIcon class="w-8 h-8 text-white" />
                      <span class="mr-2 text-white hidden md:inline-block">{{ __('Closed won') }}</span>
                    </div>

                    <div
                      class="p-4 rounded-lg bg-red-500 bg-opacity-20 backdrop-blur flex items-center justify-center shadow cursor-pointer transition-all duration-200"
                      :class="{
                        'opacity-100 scale-110 bg-opacity-70':
                          hoverTarget === 'failed',
                      }" @dragover.prevent="hoverTarget = 'failed'" @dragleave.prevent="hoverTarget = null"
                      @drop.prevent="markItem(currentDraggedItem, 'failed')">
                      <XCircleIcon class="w-8 h-8 text-white" />
                      <span class="mr-2 text-white hidden md:inline-block">{{ __('Closed lost') }}</span>
                    </div>
                  </div>
                </transition>
              </div>
            </li>
          </ul>
          <button class="btn btn-primary btn-sm mt-4 mx-auto" :class="{ 'loading': loadingBtn }" @click="getOpportunities(false)"
            v-if="!directLoading && meta.last_page > meta.current_page">
            {{ __('Load more') }}
          </button>
        </div>
      </div>
    </div>
  </div>
  <Teleport to="#app">
    <input type="checkbox" id="menu-modal" class="modal-toggle" />
    <label class="modal modal-bottom lg:modal-middle" for="menu-modal">
      <label class="modal-box !max-w-sm p-3" for="">
        <ul class="menu w-full justify-center">
          <li>
            <button @click="showOpportunity">{{ __("Show") }}</button>
          </li>
          <li>
            <button @click="setOpportunity('edit', { id: opportunity.id })">
              {{ __("Edit") }}
            </button>
          </li>
          <li>
            <button class="text-error" @click="setOpportunity('delete', { id: opportunity.id })">
              {{ __("Delete") }}
            </button>
          </li>
          <li>
            <button @click="setAppointment('index')">{{
              __("Activities")
            }}</button>
          </li>
          <li>
            <button @click="setAppointment('create')">
              {{ __("Create activity") }}
            </button>
          </li>
        </ul>
      </label>
    </label>
  </Teleport>
  <Teleport to="#app">
    <input type="checkbox" id="add-opportunity-modal" class="modal-toggle" />
    <div class="modal" for="add-opportunity-modal">
      <div class="modal-box max-w-7xl bg-base-200 min-h-40">
        <label @click="hideModal" class="btn btn-sm btn-circle btn-ghost absolute left-2 top-2">✕</label>
        <AddEditOpportunity modal="add-opportunity-modal" :opportunity="opportunity"
          v-if="hasModal('add-opportunity-modal')" />
      </div>
    </div>
  </Teleport>
  <Teleport to="#app">
    <input type="checkbox" id="opportunity-modal" class="modal-toggle" />
    <div class="modal" for="opportunity-modal">
      <div class="modal-box max-w-7xl bg-base-200 min-h-40">
        <button @click="hideModal" class="btn btn-sm btn-circle btn-ghost absolute left-2 top-2">✕</button>
        <Opportunity :id="opportunity.id" v-if="hasModal('opportunity-modal')" />
      </div>
    </div>
  </Teleport>
  <Teleport to="#app">
    <input type="checkbox" id="activity-modal" class="modal-toggle" />
    <div class="modal" for="activity-modal">
      <div class="modal-box max-w-7xl bg-base-200 min-h-40">
        <button class="btn btn-sm btn-circle btn-ghost absolute left-2 top-2" @click="hideModal">✕</button>
        <Appointments :modal="true" model_type="opportunity" :model_id="opportunity.id"
          v-if="hasModal('activity-modal')" @show="setAppointment" @edit="setAppointment" />
      </div>
    </div>
  </Teleport>
  <Teleport to="#app">
    <input type="checkbox" id="add-activity-modal" class="modal-toggle" />
    <div class="modal" for="add-activity-modal">
      <div class="modal-box max-w-7xl bg-base-200 min-h-40" for="">
        <button @click="hideModal" class="btn btn-sm btn-circle btn-ghost absolute left-2 top-2">✕</button>
        <AddEditAppointment :appointment="appointment" v-if="hasModal('add-activity-modal')"
          modal="add-activity-modal" />
      </div>
    </div>
  </Teleport>

  <Teleport to="#app">
    <input type="checkbox" id="appointment-modal" class="modal-toggle" />
    <div class="modal" for="appointment-modal">
      <div class="modal-box max-w-7xl bg-base-200 min-h-40">
        <button @click="hideModal" class="btn btn-sm btn-circle btn-ghost absolute left-2 top-2">✕</button>
        <Appointment :id="appointment.id" v-if="hasModal('appointment-modal')" />
      </div>
    </div>
  </Teleport>
  <Teleport to="#app">
    <input type="checkbox" id="failure-opportunity-modal" class="modal-toggle" />
    <div class="modal">
      <div class="modal-box bg-base-300">
        <h3 class="font-bold text-lg">{{ __('Reason for failure') }}</h3>
        <Form @submit="updateOpportunity('failure-opportunity-modal')" class="h-full flex flex-col mt-4"
          v-slot="{ errors }" v-if="hasModal('failure-opportunity-modal')">
          <div class="flex flex-col" v-if="opportunity.status == 'failed'">
            <label class="text-dark-550 dark:text-gray-810 text-sm mb-1 font-medium" for="">
              {{ __('The reason for the loss') }}
            </label>
            <Field as="select" v-model="opportunity.failure_reason_id" rules="required"
              :class="{ 'input-error': errors.failure_reason_id }" class="select" name="failure_reason_id">
              <option value="">{{ __('Select...') }}</option>
              <option v-for="item in meta.failure_reasons" :value="item.id">{{ item.title }}
              </option>
            </Field>
          </div>
          <div class="flex flex-col mt-3">
            <label class="text-dark-550 dark:text-gray-810 text-sm mb-1 font-medium" for="">
              {{ __('Description') }}
            </label>
            <Field as="textarea" v-model="opportunity.description" class="textarea" type="text" name="description" />
          </div>
          <div class="modal-action">
            <button @click="hideModal" class="btn btn-sm">{{ __('Refuse')
            }}</button>
            <button type="submit" class="btn btn-sm btn-info !ml-0 !mr-2">
              {{ __('Save') }}
            </button>
          </div>
        </Form>
      </div>
    </div>
  </Teleport>
  <Teleport to="#app">
    <input type="checkbox" id="opportunity-delete-modal" class="modal-toggle" />
    <div class="modal">
      <div class="modal-box">
        <h3 class="font-bold text-lg">{{ __('Delete opportunity') }}</h3>
        <p class="py-4">{{ __('Are you sure to delete this opportunity?') }}</p>
        <div class="modal-action">
          <label class="btn btn-sm" for="opportunity-delete-modal"
            :class="{ 'pointer-events-none opacity-60': loadingBtn }">{{
              __('Cancel') }}</label>
          <button class="btn btn-error btn-sm text-white" @click="removeOpportunityItem"
            :class="{ 'loading': loadingBtn }">
            {{ __('Delete') }}
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>
<script setup>
const {
  getWorktable,
  worktable,
  loading,
  meta,
  errors,
  remove,
  opportunities,
  opportunity,
  removeOpportunity,
  getOpportunitiesFromRoute,
  updateOpportunity,
  appointment,
  loadingBtn,
  directLoading,
} = useDashboard();
import draggable from "vuedraggable";
import {
  EllipsisVerticalIcon,
  PlusIcon,
  CheckCircleIcon,
  XCircleIcon,
} from "@heroicons/vue/24/outline";
import { Field, Form } from "vee-validate";
const modal = ref([]);
const dragging = ref(false);
const currentDraggedItem = ref(null);
const hoverTarget = ref(null);
const selectedRoute = ref({});
watch(progressDialog, (value) => {
  if (!value) {
    const m = modal.value.find(id => ['add-activity-modal', 'add-opportunity-modal', 'failure-opportunity-modal'].includes(id))
     if (modal.value.length && !document.getElementById(m)?.checked) hideModal();
     if (!modal.value.length) getData();
  }
});
watch(worktable,
  (value) => setRoute(value.find((item) => item.is_default))
);
watch(loadingBtn,
  (value) => {
    if (!value && document.getElementById('appointment-delete-modal')) getData();
  }
);
const removeOpportunityItem = () => {
  removeOpportunity(true).then((res) => {
    if (res && !res.error) {
      hideModal();
      opportunities.value = opportunities.value.filter(item => item.id != opportunity.id);
      getData();
    }
  });
}
const setItem = (item) => (opportunity.id = item.id);
const setRoute = (route) => {
  selectedRoute.value = route;
  getOpportunities(true);
};
const getOpportunities = (reset = false) => {
  getOpportunitiesFromRoute(selectedRoute.value.id, { page: (reset ? 0 : meta.current_page) + 1 })
}
const itemsByRoute = (route) => opportunities.value.filter(item => item.sale_process == route.title);
const setAppointment = (type, id = null) => {
  if (type == "create") {
    Object.assign(appointment, {
      type: "contact",
      parent_type: "opportunity",
      appointmentable_id: opportunity.id,
    });
  }
  appointment.id = id;
  const modal =
    type == "index"
      ? "activity-modal"
      : (type == "show"
        ? "appointment-modal"
        : "add-activity-modal");
  showModal(modal);
};
const showOpportunity = () => {
  showModal("opportunity-modal");
};

const setOpportunity = (type = 'create', obj = {}) => {
  Object.assign(remove, { id: obj.id });
  Object.assign(opportunity, { id: obj.id, sale_route_id: obj.route?.sale_route_id, sale_process_id: obj.route?.id });
  showModal(type == "delete" ? "opportunity-delete-modal" : "add-opportunity-modal");
};
const showModal = (id) => {
  modal.value.push(id);
  const menuModal = document.getElementById("menu-modal");
  if (menuModal.checked) menuModal.checked = false;
  document.getElementById(id).checked = true;
}
const hasModal = (id) => modal.value.includes(id);
const hideModal = () => {
  document.getElementById(modal.value[modal.value.length - 1]).checked = false;
  modal.value.pop();
}

const onDragStart = (evt) => {
  dragging.value = true;
  currentDraggedItem.value = evt.item.__vueParentComponent.props.modelValue[evt.oldIndex - 1];
};

const onDragEnd = (evt) => {
  dragging.value = false;
  currentDraggedItem.value = null;
  hoverTarget.value = null;
};

const markItem = (item, status) => {
  if (!item) return;
  item.status = status;
  Object.assign(opportunity, { id: item.id, status });
  if (status == 'failed') showModal('failure-opportunity-modal');
  else updateOpportunity('failure-opportunity-modal');
  dragging.value = false;
  currentDraggedItem.value = null;
  hoverTarget.value = null;
};
const onDragChange = (evt, route) => {
  if (evt.added?.element?.id && !hoverTarget.value) {
    Object.assign(opportunity, { id: evt.added?.element?.id, sale_process_id: route.id });
    const item = evt.added.element;
    const index = opportunities.value.findIndex(i => i.id === item.id);
    if (index !== -1) opportunities.value[index] = { ...item, sale_process: route.title };
    updateOpportunity('failure-opportunity-modal');
  }
};
const getData = () => {
  getWorktable();
};
onMounted(() => {
  getData();
});
</script>
<style>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
